import { useState, useEffect } from "react";
import PanelRefreshButton from "./PanelRefreshButton";
import { API, axios } from "@/api";
import { Bug, Loader2, CheckCircle, XCircle, Clock, Shield } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const STATUS_COLORS = {
  pending: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  open: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  acknowledged: { bg: "bg-sky-50", text: "text-sky-700", border: "border-sky-200" },
  validated: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  fix_shipped: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
  dismissed: { bg: "bg-slate-50", text: "text-slate-500", border: "border-slate-200" },
};

const ISSUE_LABELS = {
  wrong_price: "Wrong Price", dead_link: "Dead Link", offer_expired: "Offer Expired",
  business_closed: "Business Closed", wrong_info: "Wrong Info", wrong_category: "Wrong Category",
  missing_details: "Missing Details", other: "Other",
};

const TYPE_COLORS = ["#ef4444", "#f59e0b", "#8b5cf6", "#6366f1", "#0ea5e9", "#10b981", "#f97316", "#64748b"];

import { toast } from "sonner";

export default function BugBountyPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [resolving, setResolving] = useState(null);
  const [selectedBugs, setSelectedBugs] = useState(new Set());
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/admin/bug-reports?status=${filter}`);
      setData(res.data);
      setLastRefreshedAt(Date.now());
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [filter]);

  const handleResolve = async (reportId, action) => {
    setResolving(reportId);
    try {
      await axios.put(`${API}/admin/bug-reports/${reportId}`, { action });
      fetchData();
    } catch {}
    finally { setResolving(null); }
  };

  const handleBatchBugs = async (action) => {
    try {
      const res = await axios.post(`${API}/admin/batch/resolve-bugs`, { report_ids: [...selectedBugs], action });
      const actionId = res.data.action_id;
      toast.success(res.data.message, {
        action: actionId ? { label: "Undo", onClick: async () => {
          try { await axios.post(`${API}/admin/actions/${actionId}/undo`); toast.success("Undone"); fetchData(); } catch { toast.error("Undo window expired"); }
        }} : undefined,
        duration: 6000,
      });
      setSelectedBugs(new Set());
      setLoading(true);
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-6">No data</p>;

  const { stats, reports, top_reporters, by_type } = data;
  const chartData = (by_type || []).map(d => ({ ...d, label: ISSUE_LABELS[d.issue_type] || d.issue_type }));

  return (
    <div className="space-y-4" data-testid="bug-bounty-panel">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-2" data-testid="bug-stats">
        <div className="bg-white rounded-xl border border-slate-100 p-2.5 text-center">
          <p className="text-lg font-bold text-slate-700">{stats.total}</p>
          <p className="text-[10px] text-slate-400">Total</p>
        </div>
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-2.5 text-center">
          <p className="text-lg font-bold text-amber-700">{stats.pending}</p>
          <p className="text-[10px] text-amber-600">Pending</p>
        </div>
        <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-2.5 text-center">
          <p className="text-lg font-bold text-emerald-700">{stats.validated}</p>
          <p className="text-[10px] text-emerald-600">Validated</p>
        </div>
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-2.5 text-center">
          <p className="text-lg font-bold text-slate-500">{stats.dismissed}</p>
          <p className="text-[10px] text-slate-400">Dismissed</p>
        </div>
      </div>

      {/* Issue Type Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 p-3">
          <p className="text-xs font-semibold text-slate-700 mb-2">Issues by Type</p>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 9 }} />
                <YAxis dataKey="label" type="category" tick={{ fontSize: 9 }} width={80} />
                <Tooltip />
                <Bar dataKey="count" name="Reports" radius={[0, 4, 4, 0]}>
                  {chartData.map((_, i) => <Cell key={i} fill={TYPE_COLORS[i % TYPE_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top Reporters */}
      {top_reporters?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 p-3">
          <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-amber-500" /> Top Bug Hunters</p>
          <div className="space-y-1.5">
            {top_reporters.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-slate-600">{r.user_name}</span>
                <span className="font-bold text-slate-700">{r.report_count} reports</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter + Reports List */}
      <div className="flex gap-2">
        {["pending", "validated", "dismissed", "all"].map((f) => (
          <button
            key={f}
            onClick={() => { setFilter(f); setLoading(true); }}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${filter === f ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
            data-testid={`bug-filter-${f}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {/* Batch bar */}
        {selectedBugs.size > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 bg-sky-50 border border-sky-200 rounded-xl" data-testid="bugs-batch-bar">
            <span className="text-xs font-bold text-sky-700">{selectedBugs.size} selected</span>
            {(filter === "pending" || filter === "all") && (
              <button onClick={() => handleBatchBugs("acknowledge")} className="px-2.5 py-1 rounded-lg bg-sky-500 text-white text-[10px] font-bold hover:bg-sky-600" data-testid="bugs-batch-ack">Ack All</button>
            )}
            {filter === "validated" && (
              <button onClick={() => handleBatchBugs("fix_shipped")} className="px-2.5 py-1 rounded-lg bg-green-500 text-white text-[10px] font-bold hover:bg-green-600" data-testid="bugs-batch-fix">Fix Shipped All</button>
            )}
            <button onClick={() => handleBatchBugs("dismiss")} className="px-2.5 py-1 rounded-lg bg-slate-500 text-white text-[10px] font-bold hover:bg-slate-600" data-testid="bugs-batch-dismiss">Dismiss All</button>
            <button onClick={() => setSelectedBugs(new Set())} className="text-[10px] text-slate-400 hover:text-slate-600 ml-auto">Clear</button>
          </div>
        )}
        {/* Select all */}
        {reports?.length > 1 && filter !== "dismissed" && (
          <label className="flex items-center gap-2 px-1 cursor-pointer">
            <input type="checkbox" checked={reports.length > 0 && selectedBugs.size === reports.length} onChange={(e) => { e.target.checked ? setSelectedBugs(new Set(reports.map(r => r.id))) : setSelectedBugs(new Set()); }} className="w-3.5 h-3.5 rounded accent-sky-500" data-testid="bugs-select-all" />
            <span className="text-[10px] text-slate-400 font-medium">Select all ({reports.length})</span>
          </label>
        )}
        {reports?.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No {filter} reports</p>}
        {reports?.map((r) => {
          const scfg = STATUS_COLORS[r.status] || STATUS_COLORS.pending;
          return (
            <div key={r.id} className={`rounded-xl border p-3 ${scfg.border} ${scfg.bg} flex items-start gap-2`} data-testid={`bug-report-${r.id}`}>
              {filter !== "dismissed" && (
                <input type="checkbox" checked={selectedBugs.has(r.id)} onChange={(e) => { const s = new Set(selectedBugs); e.target.checked ? s.add(r.id) : s.delete(r.id); setSelectedBugs(s); }} className="w-3.5 h-3.5 mt-1 rounded accent-sky-500 flex-shrink-0" data-testid={`bug-check-${r.id}`} />
              )}
              <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-800">{r.business_name}</span>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${scfg.bg} ${scfg.text}`}>{r.status}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    <span className="font-medium">{ISSUE_LABELS[r.issue_type] || r.issue_type}</span>
                    {r.description && ` — ${r.description}`}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-1">
                    {r.user_name} · {new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    · {r.points_awarded} pts
                  </p>
                </div>
                {(r.status === "pending" || r.status === "open") && (
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => handleResolve(r.id, "acknowledge")}
                      disabled={resolving === r.id}
                      className="px-2 py-1.5 rounded-lg bg-sky-100 text-sky-700 text-[10px] font-bold hover:bg-sky-200 flex items-center gap-1"
                      data-testid={`acknowledge-${r.id}`}
                    >
                      {resolving === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />} Ack
                    </button>
                    <button
                      onClick={() => handleResolve(r.id, "dismiss")}
                      disabled={resolving === r.id}
                      className="px-2 py-1.5 rounded-lg bg-red-100 text-red-600 text-[10px] font-bold hover:bg-red-200 flex items-center gap-1"
                      data-testid={`dismiss-${r.id}`}
                    >
                      <XCircle className="w-3 h-3" /> Drop
                    </button>
                  </div>
                )}
                {r.status === "acknowledged" && (
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => handleResolve(r.id, "validate")}
                      disabled={resolving === r.id}
                      className="px-2 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 text-[10px] font-bold hover:bg-emerald-200 flex items-center gap-1"
                      data-testid={`validate-${r.id}`}
                    >
                      <CheckCircle className="w-3 h-3" /> Validate
                    </button>
                  </div>
                )}
                {r.status === "validated" && (
                  <button
                    onClick={() => handleResolve(r.id, "fix_shipped")}
                    disabled={resolving === r.id}
                    className="px-2 py-1.5 rounded-lg bg-green-100 text-green-700 text-[10px] font-bold hover:bg-green-200 flex items-center gap-1 flex-shrink-0"
                    data-testid={`fix-shipped-${r.id}`}
                  >
                    <CheckCircle className="w-3 h-3" /> Fix Shipped
                  </button>
                )}
              </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
